import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, currentWeek } from "@/lib/format";
import { ColorPill, ProgressBar } from "@/components/StatusPill";
import { WeekSelect } from "@/components/WeekSelect";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Wallet, TrendingDown, PiggyBank, Home, Clock, ReceiptText, AlertTriangle, Loader2, Plus, ChevronRight, Hourglass } from "lucide-react";

const KpiCard = ({ label, value, icon: Icon, accent, testid, onClick }) => (
  <Card data-testid={testid} onClick={onClick}
    className="p-5 border-border shadow-[0_2px_8px_-2px_rgba(15,23,42,0.08)] cursor-pointer hover:-translate-y-[2px] hover:shadow-[0_12px_32px_-4px_rgba(15,23,42,0.12)] transition-transform duration-200">
    <div className="flex items-start justify-between">
      <span className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-500">{label}</span>
      <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${accent}`}><Icon className="h-4 w-4" /></div>
    </div>
    <div className="mt-3 font-mono tabular-nums text-2xl lg:text-[1.75rem] font-semibold text-slate-900">{value}</div>
  </Card>
);

export default function Dashboard() {
  const { settings } = useAuth();
  const nav = useNavigate();
  const [week, setWeek] = useState(currentWeek());
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const cur = settings.currency || "£";

  useEffect(() => {
    setData(null);
    api.get(`/dashboard?week=${week}`).then((r) => setData(r.data));
    api.get(`/alerts?week=${week}`).then((r) => setAlerts(r.data)).catch(() => {});
  }, [week]);

  const go = (path) => nav(path);
  const openAlert = (a) => {
    if (a.type === "txn" && a.expense_id) go(`/expenses?open=${a.expense_id}`);
    else if (a.site_id) go(`/sites/${a.site_id}?week=${week}`);
  };

  if (!data) return <div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  const k = data.kpis;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-head text-2xl lg:text-3xl font-semibold text-slate-900">Dashboard</h1>
          <p className="text-slate-500 text-sm mt-1">How much we gave each home, what&apos;s spent, and what remains · {data.week_label}</p>
        </div>
        <div className="flex items-center gap-3">
          <WeekSelect value={week} onChange={setWeek} />
          <Button data-testid="dashboard-add-expense" onClick={() => go("/add")} className="bg-[#0F172A] hover:bg-[#1E293B]"><Plus className="h-4 w-4 mr-1.5" /> Add Expense</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-7 gap-4">
        <KpiCard testid="kpi-total-budget" label="Total Budget" value={money(k.total_budget, cur)} icon={Wallet} accent="bg-slate-100 text-slate-700" onClick={() => go(`/sites?week=${week}`)} />
        <KpiCard testid="kpi-total-spent" label="Approved Spend" value={money(k.total_spent, cur)} icon={TrendingDown} accent="bg-emerald-50 text-emerald-600" onClick={() => go(`/expenses?week=${week}&status=approved`)} />
        <KpiCard testid="kpi-pending-spend" label="Pending Spend" value={money(k.pending_spend, cur)} icon={Hourglass} accent="bg-sky-50 text-sky-600" onClick={() => go(`/expenses?week=${week}&status=submitted,query_raised`)} />
        <KpiCard testid="kpi-total-remaining" label="Remaining" value={money(k.total_remaining, cur)} icon={PiggyBank} accent="bg-emerald-50 text-emerald-600" onClick={() => go(`/sites?week=${week}`)} />
        <KpiCard testid="kpi-active-homes" label="Active Homes" value={k.active_homes} icon={Home} accent="bg-slate-100 text-slate-700" onClick={() => go(`/sites?week=${week}`)} />
        <KpiCard testid="kpi-awaiting" label="Awaiting Approval" value={k.awaiting_approval} icon={Clock} accent="bg-amber-50 text-amber-600" onClick={() => go(`/expenses?week=${week}&status=submitted,query_raised`)} />
        <KpiCard testid="kpi-missing-receipts" label="Missing Receipts" value={k.missing_receipts} icon={ReceiptText} accent="bg-red-50 text-red-600" onClick={() => go(`/expenses?week=${week}&missing=true`)} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border-border overflow-hidden">
          <div className="px-5 py-4 border-b border-border"><h2 className="font-head text-lg font-medium text-slate-800">Home comparison</h2></div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Home</TableHead>
                  <TableHead className="text-right">Budget</TableHead>
                  <TableHead className="text-right">Spent</TableHead>
                  <TableHead className="text-right">Remaining</TableHead>
                  <TableHead className="w-[160px]">% Used</TableHead>
                  <TableHead className="w-[40px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.sites.map((s) => (
                  <TableRow key={s.id} data-testid={`site-row-${s.code}`} className="cursor-pointer hover:bg-slate-50" onClick={() => go(`/sites/${s.id}?week=${week}`)}>
                    <TableCell className="font-medium text-slate-800">{s.name}<div className="text-xs text-slate-400">{s.staff_count} staff</div></TableCell>
                    <TableCell className="text-right font-mono tabular-nums">{money(s.allocation, cur)}</TableCell>
                    <TableCell className="text-right font-mono tabular-nums">{money(s.spent, cur)}</TableCell>
                    <TableCell className="text-right font-mono tabular-nums font-medium">{money(s.remaining, cur)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <ProgressBar pct={s.pct_used} color={s.status_color} />
                        <ColorPill color={s.status_color}>{s.pct_used}%</ColorPill>
                      </div>
                    </TableCell>
                    <TableCell><ChevronRight className="h-4 w-4 text-slate-300" /></TableCell>
                  </TableRow>
                ))}
                {data.sites.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8">No homes to display</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </Card>

        <Card className="border-border">
          <div className="px-5 py-4 border-b border-border flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              <h2 className="font-head text-lg font-medium text-slate-800">Alerts</h2>
              {alerts.length > 0 && <span className="text-xs font-semibold bg-amber-100 text-amber-700 rounded-full px-2 py-0.5">{alerts.length}</span>}
            </div>
            <button data-testid="view-all-alerts" onClick={() => go(`/expenses?week=${week}&status=submitted,query_raised`)}
              className="text-xs font-semibold text-slate-500 hover:text-slate-900 transition-colors duration-200">View all →</button>
          </div>
          <div className="p-4 space-y-2 max-h-[420px] overflow-y-auto" data-testid="alerts-panel">
            {alerts.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">No alerts for this week.</p>}
            {alerts.map((a, i) => (
              <button key={i} data-testid={`alert-${i}`} onClick={() => openAlert(a)}
                className={`w-full text-left p-3 rounded-lg border text-sm transition-colors duration-200 ${a.level === "red" ? "bg-red-50 border-red-200 text-red-800 hover:bg-red-100" : "bg-amber-50 border-amber-200 text-amber-800 hover:bg-amber-100"}`}>
                <div className="flex items-center justify-between">
                  <div className="font-medium">{a.site}</div>
                  <ChevronRight className="h-4 w-4 opacity-60" />
                </div>
                <div className="text-xs mt-0.5">{a.message}</div>
              </button>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
